import {
  computed,
  reactive,
  ref,
  watch,
} from 'vue';
import { defineStore } from 'pinia';
import { apiClient } from '@/apis';
import {
  useRoute,
  useRouter,
} from 'vue-router';
import { getImageInfo } from '@/utils/getImageInfo';
import messageTypeToSetData from '@/utils/messageTypeToSetData';
import { staticUrl } from '@/config';
import apiErrorMessage from '@/utils/apiErrorMessage';

const PERSONALIZATION_REGEX = /#\{([^}]*)\}/g;

export default defineStore('data', () => {
  const route = useRoute();
  const router = useRouter();
  const init = ref(false);
  const messageActivityName = ref('');
  const encryptType = ref('NONE');
  const loading = ref(false);

  const messageData = reactive({
    textmessage: {
      content: '',
      subject: '',
      image: {},
    },
    friendtalkDefault: {
      content: '',
      image: {},
      coupon: {},
      buttons: [],
    },
    friendtalkWideItemList: {
      index: 0,
      header: '',
      list: [
        {
          image: {},
        },
        {
          image: {},
        },
        {
          image: {},
        },
      ],
      coupon: {},
      buttons: [],
    },
    friendtalkCarouselFeed: {
      index: 0,
      list: [
        {
          image: {},
          coupon: {},
          buttons: [],
        },
        {
          image: {},
          coupon: {},
          buttons: [],
        },
      ],
      tail: {},
    },
    friendtalkCarouselCommerce: {
      index: 0,
      list: [
        {
          introEnabled: false,
          image: {},
        },
        {
          image: {},
          coupon: {},
          buttons: [],
        },
        {
          image: {},
          coupon: {},
          buttons: [],
        },
      ],
      tail: {},
    },
    friendtalkCommerce: {
      image: {},
      coupon: {},
      buttons: [],
    },
    friendtalkPremiumVideo: {
      image: {},
      coupon: {},
      buttons: [],
    },
    messageType: 'SMS',
    kakaoProfile: '',
    recipientNumber: '',
    department: '',
    isAdvert: false,
    redeliveryEnabled: false,
    alimtalkTemplateData: {
      buttons: [],
      itemList: {
        items: [],
        summary: {
          title: '',
          description: '',
        },
      },
      itemHighlight: {
        title: '',
        description: '',
      },
      header: '',
      extra: '',
      title: '',
      subTitle: '',
    },
    // reserve: {
    //   enable: false,
    //   time: '',
    // },
    fallback: {},
    adult: false,
    versionId: '',
    brandDefault: {
      image: {},
      coupon: {},
      buttons: [],
    },
    brandWideItemList: {
      index: 0,
      header: '',
      list: [],
      content: '',
      image: {},
      coupon: {},
      buttons: [],
    },
    brandCarouselFeed: {
      index: 0,
      list: [],
      tail: {},
    },
    brandCarouselCommerce: {
      index: 0,
      list: [],
      tail: {},
    },
    brandCommerce: {
      image: {},
      buttons: [],
      coupon: {},
    },
    brandPremiumVideo: {
      image: {},
      buttons: [],
      coupon: {},
    },
  });

  const pathToCache = new Map();

  const dataExtensions = reactive({
    'Event.DEAudience-4339524c-23f1-7d42-8296-94ba5161c008.Subkey': {
      name: 'Sub-key',
      type: 'Text',
      length: 50,
      default: null,
      isNullable: null,
      isPrimaryKey: true,
      disabled: true,
    },
    'Event.DEAudience-4339524c-23f1-7d42-8296-94ba5161c008.Phone': {
      name: 'Pho ne',
      type: 'Phone',
      length: 50,
      default: null,
      isNullable: true,
      isPrimaryKey: null,
      disabled: true,
    },
    'Event.DEAudience-4339524c-23f1-7d42-8296-94ba5161c008.name': {
      name: 'na_me',
      type: 'Text',
      length: 50,
      default: null,
      isNullable: true,
      isPrimaryKey: null,
    },
    'Event.DEAudience-4339524c-23f1-7d42-8296-94ba5161c007.Date': {
      name: 'Da:te',
      type: 'Text',
      length: 50,
      default: null,
      isNullable: true,
      isPrimaryKey: null,
      disabled: true,
    },
    'Event.DEAudience-4339524c-23f1-7d42-8296-94ba5161c008.Date': {
      name: 'Date',
      type: 'Text',
      length: 50,
      default: null,
      isNullable: true,
      isPrimaryKey: null,
    },
    'Event.DEAudience-4339524c-23f1-7d42-8296-94ba5161c009.Phone': {
      name: 'Phone',
      type: 'Phone',
      length: 50,
      default: null,
      isNullable: true,
      isPrimaryKey: null,
    },
    'Event.DEAudience-4339524c-23f1-7d42-8296-94ba5161c009.mobilePhone': {
      name: 'mobilePhone',
      type: 'Phone',
      length: 50,
      default: null,
      isNullable: true,
      isPrimaryKey: null,
    },
    'Event.DEAudience-4339524c-23f1-7d42-8296-94ba5161c009.PhonePhone': {
      name: 'PhonePhone',
      type: 'Phone',
      length: 50,
      default: null,
      isNullable: true,
      isPrimaryKey: null,
    },
  });

  const outgoingPhone = reactive({
    init: false,
    list: [],
  });

  const department = reactive({
    init: false,
    list: [],
  });

  const rejectPhone = reactive({
    init: false,
    list: [],
  });

  const kakaoProfile = reactive({
    init: false,
    list: [],
  });

  const organizations = reactive({
    init: false,
    data: {},
  });

  const recipientNumber = ref([]);

  const setRecipientNumberData = () => {
    const obj = dataExtensions;

    recipientNumber.value = Object.values(obj)
      .filter((value) => ['Text', 'Phone'].includes(value.type))
      .sort((a, b) => {
        const prev = (a.name || '').match(/phone/gi)?.length || 0;
        const next = (b.name || '').match(/phone/gi)?.length || 0;

        return next - prev;
      })
      .map(({
        name,
        disabled,
      }) => ({
        value: name,
        label: name,
        disabled,
      }));
  };

  watch(dataExtensions, setRecipientNumberData, { deep: true, immediate: true });

  // setRecipientNumberData();

  const setDataExtensions = (dataList) => {
    Object.keys(dataExtensions).forEach((key) => {
      delete dataExtensions[key];
    });

    dataList.forEach((item) => {
      dataExtensions[item.key] = item;
    });
  };

  const getOutgoingPhone = () => {
    if (outgoingPhone.init) {
      return outgoingPhone;
    }

    apiClient.get('/common/outgoing-phones')
      .then((response) => {
        outgoingPhone.list = response.map?.(({ phone }) => ({
          label: phone,
          value: phone,
        })) ?? [];
        outgoingPhone.init = true;
      })
      .catch((error) => {
        // TODO API 발신번호 정보를 불러올 수 없습니다. 잠시 후 다시 시도해 주세요.
        console.error(error);
      });

    return outgoingPhone;
  };

  const getDepartment = () => {
    if (department.init) {
      return department;
    }

    apiClient.get('/common/departments')
      .then((response) => {
        department.list = response.map?.(({ id, name }) => ({
          label: name,
          value: id,
        })) ?? [];

        department.init = true;
      })
      .catch((error) => {
        // TODO API 부서 정보를 불러올 수 없습니다. 잠시 후 다시 시도해 주세요.
        console.error(error);
      });

    return department;
  };

  const getRejectPhone = () => {
    if (rejectPhone.init) {
      return rejectPhone;
    }

    apiClient.get('/common/reject-phones')
      .then((response) => {
        rejectPhone.list = response.map?.(({ phone }) => ({
          label: phone,
          value: phone,
        })) ?? [];
        rejectPhone.init = true;
      })
      .catch((error) => {
        // TODO API 수신거부번호 정보를 불러올 수 없습니다. 잠시 후 다시 시도해 주세요.
        console.error(error);
      });

    return rejectPhone;
  };

  const getKakaoProfile = () => {
    if (kakaoProfile.init) {
      return kakaoProfile;
    }

    apiClient.get('/common/kakao-profiles')
      .then((response) => {
        kakaoProfile.list = response.map?.(({ name, id }) => ({
          label: name,
          value: id,
        })) ?? [];
        kakaoProfile.init = true;
      })
      .catch((error) => {
        // TODO API 카카오 프로필 정보를 불러올 수 없습니다. 잠시 후 다시 시도해 주세요.
        console.error(error);
      });

    return kakaoProfile;
  };

  const getOrganizations = () => {
    if (organizations.init) {
      return organizations;
    }

    apiClient.get('/common/organization')
      .then((response) => {
        organizations.init = true;

        organizations.data.name = response.name;
        organizations.data.serviceName = response.serviceName;
      })
      .catch((error) => {
        // TODO API 정보를 불러올 수 없습니다. 잠시 후 다시 시도해 주세요.
        console.error(error);
      });

    return organizations;
  };

  const setActivityData = async ({
    templateId,
    kakaoProfile: kakaoProfileData = '',
    outgoingPhone: outgoingPhoneData = '',
    rejectPhone: rejectPhoneData = '',
    recipientNumber: recipientNumberData = '',
    department: departmentData = '',
    messageType = 'SMS',
    lastMessageGroupVersionId,
    isAdvert,
    redeliveryEnabled,
    // reserve = {},
    fallback = {},
    adult,
    data = {},
    versionId = '',
    preTemplateId = '',
    targetingSettings,
    pushNotification = {},
    personalizationFields = [],
    additionalFields,
    encryptType: encryptTypeData,
  }) => {
    messageData.targetingSettings = targetingSettings;
    messageData.templateId = templateId;
    messageData.kakaoProfile = kakaoProfileData;
    messageData.outgoingPhone = outgoingPhoneData;
    messageData.rejectPhone = rejectPhoneData;
    messageData.recipientNumber = recipientNumberData;
    messageData.department = departmentData || '';
    messageData.messageType = messageType;
    messageData.lastMessageGroupVersionId = lastMessageGroupVersionId;
    messageData.redeliveryEnabled = redeliveryEnabled;
    messageData.versionId = versionId;
    messageData.isAdvert = isAdvert;
    // messageData.reserve = reserve;
    messageData.adult = adult?.enabled;
    messageData.isPushNotification = pushNotification?.enabled;
    let temp = { ...data };
    const personalizationFieldList = [];

    encryptType.value = encryptTypeData || 'NONE';

    if ([
      'ALIMTALK_BA',
      'ALIMTALK_EX',
      'ALIMTALK_AD',
      'ALIMTALK_MI',
    ].includes(messageType)) {
      let response = {};

      try {
        response = await apiClient.get(`/pre-template/${preTemplateId}`);
      } catch (error) {
        apiErrorMessage(error, '템플릿을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        throw new Error('pre template not found');
      }

      messageData.alimtalkTemplateData = {
        messageType: response.messageType || 'ALIMTALK_BA',
        emphasizeType: response.emphasizeType || 'NONE',
        content: response.content || '',
        security: response.security,
        buttons: response.buttons || [],
        itemList: {
          list: response?.itemList?.list || [],
          summary: {
            title: response?.itemList?.summary?.title || '',
            description: response?.itemList?.summary?.description || '',
          },
        },
        itemHighlight: {
          imageId: response?.itemHighlight?.imageId || '',
          title: response?.itemHighlight?.title || '',
          description: response?.itemHighlight?.description || '',
        },
        header: response?.header || '',
        imageId: response?.imageId || '',
        extra: response?.extra || '',
        title: response?.title || '',
        subTitle: response?.subTitle || '',
      };

      messageData.preTemplateId = preTemplateId;
      try {
        const dataString = JSON.stringify(response);
        const keys = [...new Set(dataString.match(PERSONALIZATION_REGEX))];

        keys.forEach((key, index) => {
          const stringKey = key.slice(2, key.length - 1);

          personalizationFieldList.push({
            index,
            label: key,
            value: personalizationFields[stringKey],
          });
        });
      } catch (error) {
        //
      }
    }

    if ([
      'BRANDTALK_TEXT',
      'BRANDTALK_IMAGE',
      'BRANDTALK_WIDE_IMAGE',
      'BRANDTALK_WIDE_ITEM_LIST',
      'BRANDTALK_COMMERCE',
      'BRANDTALK_CAROUSEL_FEED',
      'BRANDTALK_CAROUSEL_COMMERCE',
      'BRANDTALK_PREMIUM_VIDEO',
    ].includes(messageType)) {
      let response = {};

      try {
        response = await apiClient.get(`/pre-template/${preTemplateId}`);
      } catch (error) {
        apiErrorMessage(error, '템플릿을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        throw new Error('pre template not found');
      }

      messageData.messageType = response.messageType || 'BRANDTALK_TEXT';
      temp = {
        ...temp,
        ...response,
      };

      messageData.templateName = response.name;
      messageData.adult = response.adult?.enabled;
      messageData.brandPreTemplateId = preTemplateId;

      try {
        const dataString = JSON.stringify(response);
        const keys = [...new Set(dataString.match(PERSONALIZATION_REGEX))];

        keys.forEach((key, index) => {
          const stringKey = key.slice(2, key.length - 1);

          personalizationFieldList.push({
            index,
            label: key,
            value: personalizationFields[stringKey],
          });
        });
      } catch (error) {
        //
      }
    }

    const result = await messageTypeToSetData(temp);

    Object.entries(result).forEach(([key, value]) => {
      messageData[key] = value;
    });

    await router.isReady();

    // move page
    const routeMeta = route?.meta?.messageTypes ?? [];
    const tempFallback = {};

    if (fallback && fallback?.id) {
      tempFallback.enabled = true;
      tempFallback.messageType = fallback.messageType;
      tempFallback.outgoingPhone = fallback.outgoingPhone;
      tempFallback.rejectPhone = fallback.rejectPhone;
      tempFallback.content = fallback.content;
      tempFallback.subject = fallback.subject;
      tempFallback.image = {};

      if (fallback?.imageId) {
        const image = await getImageInfo({
          id: fallback.imageId,
          imageType: 'textmessage',
        });

        tempFallback.image = {
          ...image,
          type: 'MMS_IMAGE',
          id: fallback.imageId,
          url: `${staticUrl}/textmessage/${fallback.imageId}`,
        };
      }
    }

    let path = route?.path;
    const query = route?.query;

    if (routeMeta.includes(messageType)) {
      return;
    }

    router.getRoutes().some((item) => {
      const types = item?.meta?.messageTypes ?? [];

      if (types.includes(messageType)) {
        path = item.path;
        return true;
      }

      return false;
    });

    pathToCache.set(path, {
      messageType,
      fallback: tempFallback,
      additionalFields,
      personalizationFields: personalizationFieldList,
    });

    router.push({
      path,
      query,
    });
    init.value = true;
  };

  const extraInformation = computed(() => Object.entries(dataExtensions)
    .map(([, item]) => ({
      value: item.name,
      label: item.name,
      disabled: item.disabled,
    })));

  const setCache = (data) => {
    const {
      messageType,
      fallback,
      path,
      textmessage,
      friendtalkDefault,
      friendtalkWideItemList,
      friendtalkCarouselFeed,
      friendtalkCarouselCommerce,
      friendtalkCommerce,
      friendtalkPremiumVideo,
      personalizationFields,
      preTemplateId,
      alimtalkTemplateData,
      additionalFields,
      brandDefault,
      brandWideItemList,
      brandCarouselFeed,
      brandCarouselCommerce,
      brandCommerce,
      brandPremiumVideo,
      brandPreTemplateId,
      isPushNotification,
      targetingSettings,
      templateName,
    } = data;

    messageData.kakaoProfile = data.kakaoProfile;
    messageData.recipientNumber = data.recipientNumber;
    messageData.department = data.department;
    // messageData.additionalFields = additionalFields;

    switch (messageType) {
      case 'FRIENDTALK_TEXT':
      case 'FRIENDTALK_IMAGE':
      case 'FRIENDTALK_WIDE_IMAGE':
      case 'FRIENDTALK_WIDE_ITEM_LIST':
      case 'FRIENDTALK_COMMERCE':
      case 'FRIENDTALK_CAROUSEL_FEED':
      case 'FRIENDTALK_CAROUSEL_COMMERCE':
      case 'FRIENDTALK_PREMIUM_VIDEO':
        messageData.friendtalkDefault = friendtalkDefault;
        messageData.friendtalkWideItemList = friendtalkWideItemList;
        messageData.friendtalkCarouselFeed = friendtalkCarouselFeed;
        messageData.friendtalkCarouselCommerce = friendtalkCarouselCommerce;
        messageData.friendtalkCommerce = friendtalkCommerce;
        messageData.friendtalkPremiumVideo = friendtalkPremiumVideo;
        messageData.adult = data.adult;
        messageData.isPushNotification = isPushNotification;
        messageData.targetingSettings = targetingSettings;
        break;
      case 'BRANDTALK_TEXT':
      case 'BRANDTALK_IMAGE':
      case 'BRANDTALK_WIDE_IMAGE':
      case 'BRANDTALK_WIDE_ITEM_LIST':
      case 'BRANDTALK_CAROUSEL_FEED':
      case 'BRANDTALK_CAROUSEL_COMMERCE':
      case 'BRANDTALK_COMMERCE':
      case 'BRANDTALK_PREMIUM_VIDEO':
      case '':
        messageData.brandDefault = brandDefault;
        messageData.brandWideItemList = brandWideItemList;
        messageData.brandCarouselFeed = brandCarouselFeed;
        messageData.brandCarouselCommerce = brandCarouselCommerce;
        messageData.brandCommerce = brandCommerce;
        messageData.brandPremiumVideo = brandPremiumVideo;
        messageData.adult = data.adult;
        messageData.brandPreTemplateId = brandPreTemplateId;
        messageData.brandPreTemplateId = brandPreTemplateId;
        messageData.isPushNotification = isPushNotification;
        messageData.targetingSettings = targetingSettings;
        messageData.templateName = templateName;
        messageData.brandMessageType = messageType;
        break;
      case 'ALIMTALK_BA':
      case 'ALIMTALK_EX':
      case 'ALIMTALK_AD':
      case 'ALIMTALK_MI':
        messageData.preTemplateId = preTemplateId;
        messageData.alimtalkTemplateData = {
          messageType: alimtalkTemplateData.messageType || 'ALIMTALK_BA',
          emphasizeType: alimtalkTemplateData.emphasizeType || 'NONE',
          content: alimtalkTemplateData.content || '',
          security: alimtalkTemplateData.security,
          buttons: (alimtalkTemplateData.buttons || []).map((e) => e) || [],
          itemList: {
            list: (alimtalkTemplateData?.itemList?.list || []).map((e) => e) || [],
            summary: {
              title: alimtalkTemplateData?.itemList?.summary?.title || '',
              description: alimtalkTemplateData?.itemList?.summary?.description || '',
            },
          },
          itemHighlight: {
            imageId: alimtalkTemplateData?.itemHighlight?.imageId || '',
            title: alimtalkTemplateData?.itemHighlight?.title || '',
            description: alimtalkTemplateData?.itemHighlight?.description || '',
          },
          header: alimtalkTemplateData?.header || '',
          imageId: alimtalkTemplateData?.imageId || '',
          extra: alimtalkTemplateData?.extra || '',
          title: alimtalkTemplateData?.title || '',
          subTitle: alimtalkTemplateData?.subTitle || '',
        };
        break;
      case 'SMS':
      case 'MMS':
      case 'LMS':
      default:
        messageData.textmessage = textmessage;
        messageData.isAdvert = data.isAdvert;
        messageData.redeliveryEnabled = data.redeliveryEnabled;
        break;
    }

    pathToCache.set(path, {
      messageType,
      fallback,
      additionalFields,
      personalizationFields,
    });
  };

  const changeMessageType = (value) => {
    messageData.messageType = value;
  };

  const getKakaoProfileName = (item) => {
    let result = item || '';

    (kakaoProfile.value || []).some(({ label, value }) => {
      if (item === value) {
        result = label;
        return true;
      }

      return false;
    });

    return result;
  };

  const getOutgoingPhoneNumber = (item) => {
    let result = item || '';

    (outgoingPhone.value || []).some(({ label, value }) => {
      if (item === value) {
        result = label;
        return true;
      }

      return false;
    });

    return result;
  };

  const getDepartmentName = (item) => {
    let result = item || '';

    (department.value || []).some(({ label, value }) => {
      if (item === value) {
        result = label;
        return true;
      }

      return false;
    });

    return result;
  };

  const setMessageActivityName = (value) => {
    messageActivityName.value = value;
  };

  const setLoading = (value) => {
    loading.value = value;
  };

  const setEncryptType = (value) => {
    encryptType.value = value;
  };

  // init data
  // getOrganizations();
  // getKakaoProfile();
  // getRejectPhone();
  // getOutgoingPhone();

  return {
    dataExtensions,
    setDataExtensions,
    getOutgoingPhone,
    recipientNumber,
    getDepartment,
    extraInformation,
    rejectPhone,
    getRejectPhone,
    getKakaoProfile,
    getOrganizations,
    messageData,
    setActivityData,
    init,
    setCache,
    changeMessageType,
    pathToCache,
    getKakaoProfileName,
    getDepartmentName,
    getOutgoingPhoneNumber,
    messageActivityName,
    setMessageActivityName,
    loading,
    setLoading,
    encryptType,
    setEncryptType,
  };
});
